import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, X as XIcon, ArrowRight, Loader2, Clock, CheckCircle2, XCircle } from 'lucide-react'
import {
  listPendingForMe,
  listAllRequests,
  approveRequest,
  rejectRequest,
  forwardRequest,
  listApprovers,
  type LeaveRequest,
  type LeaveType,
} from '@/api/leaves'
import { useAuthStore } from '@/stores/authStore'
import { useMyCaps } from '@/lib/capabilities'
import LeaveBalancesReport from '@/components/LeaveBalancesReport'

const LEAVE_LABEL: Record<LeaveType, string> = {
  casual: 'Casual Leave', sick: 'Sick Leave', urgent: 'Urgent Leave', annual: 'Annual Leave', wfh: 'WFH',
}

function StatusChip({ status }: { status: LeaveRequest['status'] }) {
  const map: Record<LeaveRequest['status'], { label: string; cls: string; icon: any }> = {
    pending: { label: 'Pending', cls: 'bg-amber-50 text-amber-700', icon: Clock },
    approved: { label: 'Approved', cls: 'bg-green-50 text-green-700', icon: CheckCircle2 },
    rejected: { label: 'Rejected', cls: 'bg-red-50 text-red-700', icon: XCircle },
    cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-600', icon: XIcon },
  }
  const m = map[status]
  const Icon = m.icon
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  )
}

function ActionModal({
  kind, req, onClose, onConfirm,
}: { kind: 'approve' | 'reject' | 'forward' | null; req: LeaveRequest; onClose: () => void; onConfirm: (v: { comment?: string; forward_to_id?: string }) => void }) {
  const [comment, setComment] = useState('')
  const [search, setSearch] = useState('')
  const [forwardId, setForwardId] = useState('')
  const { user } = useAuthStore()

  // Only people who may approve this employee's leave can receive a forward.
  const { data: approvers = [] } = useQuery({
    queryKey: ['leaves', 'approvers', req.user_id, search],
    queryFn: () => listApprovers(search || undefined, req.user_id),
    enabled: kind === 'forward',
    staleTime: 30_000,
  })
  const forwardTargets = approvers.filter((u) => u.id !== user?.id)

  if (!kind) return null
  const title = kind === 'approve' ? 'Approve request' : kind === 'reject' ? 'Reject request' : 'Forward request'

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XIcon className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {kind === 'forward' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Forward to</label>
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..."
                className="input mb-2" />
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {forwardTargets.length === 0 && (
                  <div className="px-3 py-4 text-sm text-gray-400 text-center">No other approvers available</div>
                )}
                {forwardTargets.map((u) => (
                  <button key={u.id} type="button" onClick={() => setForwardId(u.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 text-left ${forwardId === u.id ? 'bg-[#EC5B2C]/10' : ''}`}>
                    <div className="h-7 w-7 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold">
                      {u.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{u.name}</p>
                      <p className="text-xs text-gray-500 truncate">{u.email}</p>
                    </div>
                    {forwardId === u.id && <Check className="h-4 w-4 text-[#EC5B2C]" />}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Comment (optional)</label>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
              className="textarea" />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
            <button
              onClick={() => onConfirm({ comment: comment || undefined, forward_to_id: forwardId || undefined })}
              disabled={kind === 'forward' && !forwardId}
              className={`px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 ${
                kind === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                kind === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function RequestCard({ req, onAction }: { req: LeaveRequest; onAction?: (action: 'approve' | 'reject' | 'forward', req: LeaveRequest) => void }) {
  const dateText = req.start_date === req.end_date ? req.start_date : `${req.start_date} → ${req.end_date}`
  return (
    <div className="card p-5 space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900">{req.user_name}</span>
            <span className="text-sm text-gray-500">requested</span>
            <span className="text-sm font-medium text-gray-900">{LEAVE_LABEL[req.type] || req.type}</span>
            <StatusChip status={req.status} />
            {req.is_backdated && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600" title="Applied for a day that had already passed">Backdated</span>
            )}
            {req.over_limit && (
              <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600" title="Exceeds the employee's monthly allowance — exception request">⚠ Over monthly limit</span>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-1">
            {dateText} · {req.working_days} day{req.working_days !== 1 ? 's' : ''}
            {req.half_day && ` (½ ${req.half_day_slot})`}
          </p>
          {req.reason && <p className="text-sm text-gray-500 mt-1 italic">"{req.reason}"</p>}
        </div>
      </div>

      {/* Chain */}
      {req.steps.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap text-xs">
          {req.steps.map((s, i) => (
            <div key={s.step_number} className="flex items-center gap-2">
              {i > 0 && <ArrowRight className="h-3 w-3 text-gray-400" />}
              <span className={`px-2 py-0.5 rounded-full border ${
                s.action === 'approve' ? 'bg-green-50 text-green-700 border-green-200' :
                s.action === 'reject' ? 'bg-red-50 text-red-700 border-red-200' :
                s.action === 'forward' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                'bg-amber-50 text-amber-700 border-amber-200'
              }`}>
                {s.approver_name} {s.action ? `· ${s.action}` : '· pending'}
              </span>
            </div>
          ))}
        </div>
      )}

      {onAction && req.status === 'pending' && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-50">
          <button onClick={() => onAction('approve', req)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg">
            <Check className="h-4 w-4" /> Approve
          </button>
          <button onClick={() => onAction('reject', req)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg">
            <XIcon className="h-4 w-4" /> Reject
          </button>
          <button onClick={() => onAction('forward', req)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">
            <ArrowRight className="h-4 w-4" /> Forward
          </button>
        </div>
      )}
    </div>
  )
}

export default function ManageLeaves() {
  const queryClient = useQueryClient()
  // "All Requests" is for leave managers (admins + roles with Manage Leave).
  const { has } = useMyCaps()
  const canManage = has('manage_leave')
  const [tab, setTab] = useState<'pending' | 'all' | 'balances'>('pending')
  const [modal, setModal] = useState<{ action: 'approve' | 'reject' | 'forward'; req: LeaveRequest } | null>(null)

  const { data: pending = [] } = useQuery({ queryKey: ['leaves', 'pending'], queryFn: listPendingForMe })
  const { data: all = [] } = useQuery({
    queryKey: ['leaves', 'all'], queryFn: () => listAllRequests(), enabled: canManage && tab === 'all',
  })

  const approveMut = useMutation({ mutationFn: ({ id, comment }: any) => approveRequest(id, comment),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leaves'] }) })
  const rejectMut = useMutation({ mutationFn: ({ id, comment }: any) => rejectRequest(id, comment),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leaves'] }) })
  const forwardMut = useMutation({ mutationFn: ({ id, forward_to_id, comment }: any) => forwardRequest(id, forward_to_id, comment),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leaves'] }) })

  const handleConfirm = (v: { comment?: string; forward_to_id?: string }) => {
    if (!modal) return
    const { action, req } = modal
    if (action === 'approve') approveMut.mutate({ id: req.id, comment: v.comment })
    else if (action === 'reject') rejectMut.mutate({ id: req.id, comment: v.comment })
    else if (action === 'forward' && v.forward_to_id)
      forwardMut.mutate({ id: req.id, forward_to_id: v.forward_to_id, comment: v.comment })
    setModal(null)
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Manage Leaves</h1>
        <p className="text-sm text-gray-500">Review and act on leave requests</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-6">
          <button onClick={() => setTab('pending')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              tab === 'pending' ? 'border-[#EC5B2C] text-[#EC5B2C]' : 'border-transparent text-gray-500 hover:text-gray-900'
            }`}>
            Pending My Approval {pending.length > 0 && <span className="ml-1 px-1.5 py-0.5 bg-[#EC5B2C] text-white rounded-full text-[10px]">{pending.length}</span>}
          </button>
          {canManage && (
            <button onClick={() => setTab('all')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                tab === 'all' ? 'border-[#EC5B2C] text-[#EC5B2C]' : 'border-transparent text-gray-500 hover:text-gray-900'
              }`}>
              All Requests
            </button>
          )}
          {canManage && (
            <button onClick={() => setTab('balances')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                tab === 'balances' ? 'border-[#EC5B2C] text-[#EC5B2C]' : 'border-transparent text-gray-500 hover:text-gray-900'
              }`}>
              Leave Balances
            </button>
          )}
        </div>
      </div>

      {tab === 'balances' && canManage && <LeaveBalancesReport />}

      {/* List */}
      <div className="space-y-3">
        {tab === 'pending' && (
          pending.length === 0 ? (
            <div className="bg-white border border-gray-100 rounded-xl p-12 text-center">
              <CheckCircle2 className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No pending approvals</p>
            </div>
          ) : (
            pending.map((r) => (
              <RequestCard key={r.id} req={r} onAction={(action, req) => setModal({ action, req })} />
            ))
          )
        )}
        {tab === 'all' && canManage && (
          all.length === 0 ? (
            <div className="bg-white border border-gray-100 rounded-xl p-12 text-center text-sm text-gray-500">No requests</div>
          ) : (
            all.map((r) => <RequestCard key={r.id} req={r} />)
          )
        )}
      </div>

      {modal && (
        <ActionModal kind={modal.action} req={modal.req} onClose={() => setModal(null)} onConfirm={handleConfirm} />
      )}
    </div>
  )
}
